import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';
import eventBus from '../utils/eventBus';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initialCheckDone, setInitialCheckDone] = useState(false);
    
    // Flag to prevent logout events during initial auth check
    // This prevents race conditions where a 401 during refresh triggers logout
    // before the interceptor can complete the token refresh
    const isCheckingAuthRef = React.useRef(true);

    // Add a stable user setter to prevent rapid state changes
    const setUserStable = (userData) => {
        setUser(prevUser => {
            // If no new user data, set to null
            if (!userData) {
                return null;
            }
            
            // If user data is the same, don't update state
            if (prevUser && userData && 
                prevUser.id === userData.id && 
                JSON.stringify(prevUser.roles) === JSON.stringify(userData.roles)) {
                return prevUser;
            }
            return userData;
        });
    };

    // CSRF token initialization is handled by api.js module load
    // No need to duplicate it here - removed to prevent race conditions

    useEffect(() => {
        const handleLogout = () => {
            // Ignore logout events during initial auth check
            // The interceptor may trigger events while refreshing tokens
            if (isCheckingAuthRef.current) {
                return;
            }
            setUser(null);
        };

        const handleAuthExpired = () => {
            // Ignore auth expired events during initial auth check
            if (isCheckingAuthRef.current) {
                return;
            }
            setUser(null);
        };

        eventBus.on('auth:logout', handleLogout);
        eventBus.on('auth:expired', handleAuthExpired);

        return () => {
            eventBus.off('auth:logout', handleLogout);
            eventBus.off('auth:expired', handleAuthExpired);
        };
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            isCheckingAuthRef.current = true;
            try {
                const userData = await authService.getCurrentUser();
                setUserStable(userData);
            } catch (error) {
                setUser(null);
            } finally {
                isCheckingAuthRef.current = false;
                setLoading(false);
                setInitialCheckDone(true);
            }
        };

        if (!initialCheckDone) {
            checkAuth();
        }
    }, [initialCheckDone]);

    const login = async (credentials) => {
        try {
            const loginResponse = await authService.login(credentials);
            
            // Check if 2FA is required
            if (loginResponse.requiresTwoFactor) {
                // Return the response so the UI can show 2FA challenge
                return {
                    requiresTwoFactor: true,
                    twoFactorToken: loginResponse.twoFactorToken
                };
            }
            
            // After successful login, fetch the complete user profile to get all fields including images
            const completeUserData = await authService.getCurrentUser();
            
            await new Promise((resolve) => {
                setUserStable(completeUserData);
                // Use a timeout to ensure state is updated
                setTimeout(resolve, 50);
            });
            return completeUserData;
        } catch (error) {
            throw error;
        }
    };
    
    const completeTwoFactorLogin = async () => {
        try {
            // After successful 2FA verification (cookies set by the API), fetch the complete user profile
            const completeUserData = await authService.getCurrentUser();
            
            await new Promise((resolve) => {
                setUserStable(completeUserData);
                setTimeout(resolve, 50);
            });
            return completeUserData;
        } catch (error) {
            throw error;
        }
    };

    const signup = async (userData) => {
        try {
            await authService.signup(userData);
            
            // After successful signup, fetch the complete user profile to get all fields including images
            const completeUserData = await authService.getCurrentUser();
            
            await new Promise((resolve) => {
                setUserStable(completeUserData);
                // Use a timeout to ensure state is updated
                setTimeout(resolve, 50);
            });
            return completeUserData;
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } finally {
            setUser(null);
        }
    };

    const refreshToken = async (silent = false) => {
        try {
            const userData = await authService.refreshToken(silent);
            setUserStable(userData);
            return userData;
        } catch (error) {
            if (!silent) {
                setUser(null);
            }
            throw error;
        }
    };

    const getToken = () => {
        // The actual JWT token is stored in HTTP-only cookies
        // We just need to check if the user is authenticated
        return currentUser ? true : null;
    };

    const updateUser = (updatedUserData) => {
        setUser(prev => {
            // Create a completely new object to ensure React detects the change
            const newUser = { ...prev, ...updatedUserData };
            
            // Note: CloudFront signed URLs already have unique signatures per request
            // DO NOT add cache-busting parameters (&cb=...) to signed URLs as this
            // breaks the signature and causes 403 errors.
            
            // Copy maps as new objects to ensure reference change
            if (updatedUserData.profilePictures) {
                newUser.profilePictures = { ...updatedUserData.profilePictures };
            }
            
            if (updatedUserData.banners) {
                newUser.banners = { ...updatedUserData.banners };
            }
            
            return newUser;
        });
    };

    const value = {
        currentUser,
        loading,
        login,
        completeTwoFactorLogin,
        logout,
        signup,
        refreshToken,
        getToken,
        updateUser,
        isAuthenticated: !!currentUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};